import React from 'react';
import {
  Grid,
  Paper,
  Box,
  IconButton,
  Checkbox,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import {
  ZoomIn,
  CheckCircle,
  RadioButtonUnchecked,
  DragIndicator,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Star as StarIcon
} from '@mui/icons-material';

/**
 * Reusable ImageGrid component for displaying images in Bloc components
 * Handles filtering, drag & drop, selection, validation, and display
 */
export default function ImageGrid({
  images = [],
  imageFilter = 'validated', // 'all', 'validated', 'not-validated'
  isDeleteMode = false,
  selectedImageIndices = new Set(),
  pendingImageValidations = new Map(),
  draggedImageIndex = null,
  dragOverIndex = null,
  getEffectiveValidationStatus,
  getOriginalIndex,
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDragEnd,
  handleToggleImageSelection,
  handleToggleImageValidation,
  handleOpenRegenerateDialog,
  onImageClick, // Optional custom click handler
  imageUrlField = 'img_url', // Field name for image URL: 'img_url', 'original_url', or 'url'
  // Menu props
  imageMenuAnchor = null,
  onMenuOpen = null,
  onMenuClose = null,
  onFilterChange = null,
  onDeleteModeToggle = null
}) {
  // Filter images based on filter type
  const filteredImages = images.filter((image, index) => {
    if (imageFilter === 'all') return true;
    const isValidated = getEffectiveValidationStatus ? getEffectiveValidationStatus(index) : (image.status_seance === 'validated');
    if (imageFilter === 'validated') return isValidated;
    if (imageFilter === 'not-validated') return !isValidated;
    return true;
  });

  // Get original index helper if not provided
  const getOriginalIndexHelper = (filteredIndex) => {
    if (getOriginalIndex) {
      return getOriginalIndex(filteredIndex);
    }
    // Fallback implementation
    let count = 0;
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      const isValidated = image.status_seance === 'validated';
      let shouldInclude = true;
      if (imageFilter === 'validated') shouldInclude = isValidated;
      if (imageFilter === 'not-validated') shouldInclude = !isValidated;
      
      if (shouldInclude) {
        if (count === filteredIndex) return i;
        count++;
      }
    }
    return filteredIndex;
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', minHeight: filteredImages.length === 0 ? '100px' : 'auto' }}>
      {/* Menu button - Top right - Always show when there are images, even if filtered list is empty */}
      {images && images.length > 0 && onMenuOpen && (
        <IconButton
          size="small"
          onClick={onMenuOpen}
          sx={{ 
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 20,
            color: '#6b7280',
            bgcolor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            '&:hover': { 
              backgroundColor: '#f3f4f6',
              color: '#374151'
            }
          }}
        >
          <MoreVertIcon />
        </IconButton>
      )}
      
      <Grid container spacing={2}>
      {filteredImages.map((image, filteredIndex) => {
        const originalIndex = getOriginalIndexHelper(filteredIndex);
        const isSelected = selectedImageIndices.has(originalIndex);
        const isValidated = getEffectiveValidationStatus 
          ? getEffectiveValidationStatus(originalIndex) 
          : (image.status_seance === 'validated');
        const hasPendingChange = pendingImageValidations.has(originalIndex);
        const pendingStatus = pendingImageValidations.get(originalIndex);
        
        // Get image URL from various possible field names
        const imageUrl = image[imageUrlField] || image.original_url || image.img_url || image.url;
        
        return (
          <Grid 
            item 
            xs={6} 
            sm={4} 
            md={3} 
            key={imageUrl || image.image_index || originalIndex} 
            sx={{ overflow: 'visible', position: 'relative' }}
          >
            <Paper
              draggable={!isDeleteMode && imageFilter === 'all'}
              onDragStart={(e) => handleDragStart && handleDragStart(e, originalIndex)}
              onDragOver={(e) => handleDragOver && handleDragOver(e, originalIndex)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop && handleDrop(e, originalIndex)}
              onDragEnd={handleDragEnd}
              sx={{
                position: 'relative',
                borderRadius: '8px',
                overflow: 'visible',
                cursor: isDeleteMode && isValidated ? 'not-allowed' : (draggedImageIndex === originalIndex ? 'grabbing' : 'grab'),
                border: isSelected ? '3px solid #4A90E2' : isValidated ? '2px solid #10b981' : '1px solid #e1e8ed',
                boxShadow: hasPendingChange ? '0 0 0 2px #FFA500' : (dragOverIndex === originalIndex ? '0 0 0 3px #4A90E2' : 'none'),
                opacity: isDeleteMode && isValidated ? 0.6 : (draggedImageIndex === originalIndex ? 0.5 : 1),
                transform: dragOverIndex === originalIndex ? 'scale(1.05)' : 'scale(1)',
                '&:hover': {
                  boxShadow: isDeleteMode ? 'none' : 4,
                  '& .image-overlay': {
                    opacity: isDeleteMode ? 0 : 1
                  }
                },
                transition: 'all 0.3s ease'
              }}
              onClick={() => {
                if (onImageClick) {
                  onImageClick(originalIndex, isDeleteMode, isValidated);
                } else if (isDeleteMode) {
                  // Don't allow selection of validated images
                  if (!isValidated && handleToggleImageSelection) {
                    handleToggleImageSelection(originalIndex);
                  }
                } else if (handleOpenRegenerateDialog) {
                  handleOpenRegenerateDialog(originalIndex);
                }
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '150px',
                  overflow: 'hidden',
                  borderRadius: '8px'
                }}
              >
                <Box
                  component="img"
                  src={imageUrl}
                  alt={image.description || `Image ${originalIndex + 1}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error';
                  }}
                />
                <Box
                  className="image-overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    zIndex: 1
                  }}
                >
                  <ZoomIn sx={{ color: 'white', fontSize: '2rem' }} />
                </Box>
                {image.description && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      p: 1,
                      fontSize: '0.7rem',
                      maxHeight: '40px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      zIndex: 2
                    }}
                  >
                    {typeof image.description === 'string' 
                      ? (image.description.length > 60 ? image.description.substring(0, 60) + '...' : image.description)
                      : String(image.description || '').substring(0, 60) + '...'}
                  </Box>
                )}
              </Box>
              
              {/* Favorite star icon - top left corner */}
              {image.favourite === true && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    left: 4,
                    zIndex: 13,
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  <StarIcon sx={{ color: '#FFD700', fontSize: '20px' }} />
                </Box>
              )}
              
              {/* Drag indicator - show when not in delete mode and showing all images */}
              {!isDeleteMode && imageFilter === 'all' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: image.favourite === true ? 32 : 4,
                    left: 4,
                    zIndex: 12,
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '4px',
                    p: 0.5,
                    cursor: 'grab',
                    '&:active': {
                      cursor: 'grabbing'
                    },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <DragIndicator sx={{ color: '#6b7280', fontSize: '18px' }} />
                </Box>
              )}
              
              {/* Selection checkbox - only show in delete mode and for non-validated images */}
              {isDeleteMode && !isValidated && (
                <Checkbox
                  checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (handleToggleImageSelection) {
                      handleToggleImageSelection(originalIndex);
                    }
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    // onChange is handled by onClick
                  }}
                  sx={{
                    position: 'absolute',
                    top: image.favourite === true ? 32 : 4,
                    left: 4,
                    zIndex: 15,
                    bgcolor: 'white',
                    pointerEvents: 'auto',
                    '&:hover': {
                      bgcolor: '#F5F5F5'
                    }
                  }}
                />
              )}
              
              {/* Validation button in top-right corner - Hide in delete mode */}
              {!isDeleteMode && handleToggleImageValidation && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleImageValidation(originalIndex);
                  }}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    zIndex: 10,
                    bgcolor: 'white',
                    width: '24px',
                    height: '24px',
                    minWidth: '24px',
                    padding: 0,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    '&:hover': {
                      bgcolor: '#F5F5F5',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  {isValidated ? (
                    <CheckCircle sx={{ color: '#27AE60', fontSize: '24px' }} />
                  ) : (
                    <RadioButtonUnchecked sx={{ color: '#95A5A6', fontSize: '24px' }} />
                  )}
                </IconButton>
              )}
              
              {/* Validation badge - Always visible when validated */}
              {isValidated && (
                <Chip
                  label="Validée"
                  size="small"
                  icon={<CheckCircle />}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    zIndex: 5,
                    bgcolor: '#10b981',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.7rem'
                  }}
                />
              )}
              
              {/* Pending change indicator */}
              {hasPendingChange && (
                <Chip
                  label={pendingStatus === 'validated' ? 'À valider' : 'À dévalider'}
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 4,
                    zIndex: 5,
                    bgcolor: '#FFA500',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.7rem'
                  }}
                />
              )}
            </Paper>
          </Grid>
        );
      })}
      </Grid>
      
      {/* Empty state message when filter returns no results */}
      {filteredImages.length === 0 && images.length > 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 4, 
          color: '#6b7280',
          minHeight: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {imageFilter === 'validated' && 'Aucune image validée'}
            {imageFilter === 'not-validated' && 'Aucune image non validée'}
            {imageFilter === 'all' && 'Aucune image'}
          </Typography>
        </Box>
      )}
      
      {/* Image Menu */}
      {onMenuClose && (
        <Menu
          anchorEl={imageMenuAnchor}
          open={Boolean(imageMenuAnchor)}
          onClose={onMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {/* Filter options */}
          {onFilterChange && (
            <>
              <MenuItem 
                onClick={() => {
                  onFilterChange('all');
                  onMenuClose();
                }}
                selected={imageFilter === 'all'}
              >
                <ListItemIcon>
                  <ZoomIn sx={{ fontSize: '1.2rem', color: '#4A90E2' }} />
                </ListItemIcon>
                <ListItemText primary={`Toutes les images (${images.length})`} />
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  onFilterChange('validated');
                  onMenuClose();
                }}
                selected={imageFilter === 'validated'}
              >
                <ListItemIcon>
                  <CheckCircle sx={{ fontSize: '1.2rem', color: '#27AE60' }} />
                </ListItemIcon>
                <ListItemText primary={`Validées (${images.filter((img, idx) => getEffectiveValidationStatus ? getEffectiveValidationStatus(idx) : (img.status_seance === 'validated')).length})`} />
              </MenuItem>
              <MenuItem 
                onClick={() => {
                  onFilterChange('not-validated');
                  onMenuClose();
                }}
                selected={imageFilter === 'not-validated'}
              >
                <ListItemIcon>
                  <RadioButtonUnchecked sx={{ fontSize: '1.2rem', color: '#E67E22' }} />
                </ListItemIcon>
                <ListItemText primary={`Non validées (${images.filter((img, idx) => getEffectiveValidationStatus ? !getEffectiveValidationStatus(idx) : (img.status_seance !== 'validated')).length})`} />
              </MenuItem>
            </>
          )}
          
          {/* Delete option */}
          {!isDeleteMode && onDeleteModeToggle && (
            <>
              <MenuItem divider />
              <MenuItem 
                onClick={() => {
                  onDeleteModeToggle();
                  onMenuClose();
                }}
                sx={{ color: '#E53935' }}
              >
                <ListItemIcon>
                  <DeleteIcon sx={{ fontSize: '1.2rem', color: '#E53935' }} />
                </ListItemIcon>
                <ListItemText primary="Supprimer des images" />
              </MenuItem>
            </>
          )}
        </Menu>
      )}
    </Box>
  );
}

